import { GridSortModel } from '@components/DataTable';
import { getYouTubeVideoId } from '@lib/utils/youtube';
import { isProbablyImage } from '@pages/Library/utils/mediaStatus';
import { Media } from '@typings/Media';

/**
 * Shared library media sort comparator.
 *
 * The library table (DataTable) runs with `sortingMode="server"`, so MUI does
 * NOT sort — we do, client-side. Both the store (`LibraryFolder`, which sorts
 * `files`/`filesForTable`) and the table hook (`useMediaData`, which re-sorts the
 * merged media + virtual-upload rows) both sort through these helpers, so those
 * two can't drift.
 *
 * NOTE: the grid/list view (`LibraryContentMediaList`) still has its own partial
 * comparator to position its virtual-upload cards; it consumes the already-sorted
 * `folder.files`, so real media stays correctly ordered, but that copy isn't
 * folded in here yet (its row shape differs).
 *
 * To add a new sortable column: add a case here AND mark the column `sortable`
 * in `ColumnDefinitions.ts`.
 *
 * Private on purpose — callers sort via `sortMedia`. (Don't reuse this to read a
 * *display* value; the DataGrid column `valueGetter`s own presentation, and this
 * returns a sort key that can differ, e.g. the Source column.)
 */
function getFieldValue(media: Media, field: string): string | number | null {
  switch (field) {
    case 'filename':
      return media.metadata?.name ?? media.metadata?.filename ?? null;
    case 'duration':
      return isProbablyImage(media) ? null : (media.metadata?.video?.duration ?? null);
    case 'size':
      return media.metadata?.video?.originalMediaSize ?? null;
    case 'createdAt':
      return media.createdAt ?? null;
    case 'publishedAt':
      return media.publishedAt ?? null;
    case 'updatedAt':
      return media.updatedAt ?? null;
    case 'status':
      return media.status ?? null;
    case 'channel':
      return media.origin?.source === 'external' ? media.origin.type : 'upload';
    case 'frameCount':
      return media.metadata?.video?.frameCount ?? null;
    case 'width':
      return media.metadata?.video?.width ?? null;
    case 'height':
      return media.metadata?.video?.height ?? null;
    case 'youtubeId':
      return getYouTubeVideoId(media) ?? null;
    case 'isYouTubeShort':
      return media.userMetadata?.isYoutubeShort === true ? 1 : 0;
    default:
      return null;
  }
}

/**
 * Sort media by the first entry of a DataTable sort model. Missing values
 * (`null`) always sort last, regardless of direction. Returns a new array;
 * generic so it preserves the caller's row type (e.g. `MediaTableRow`).
 */
export function sortMedia<T extends Media>(files: T[], sortModel: GridSortModel): T[] {
  if (sortModel.length === 0) return files;

  const { field, sort } = sortModel[0];
  const direction = sort === 'asc' ? 1 : -1;

  return [...files].sort((a, b) => {
    const valA = getFieldValue(a, field);
    const valB = getFieldValue(b, field);

    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;

    const cmp =
      typeof valA === 'string'
        ? valA.localeCompare(valB as string)
        : (valA as number) - (valB as number);

    return cmp * direction;
  });
}
